import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

interface WebhookEvent {
  id: string;
  eventId: string;
  eventType: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  error: string | null;
  createdAt: string;
  processedAt: string | null;
}

interface WebhookEventsListProps {
  organizationId?: string;
}

export function WebhookEventsList({ organizationId }: WebhookEventsListProps) {
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<WebhookEvent | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (eventTypeFilter !== 'all') params.set('eventType', eventTypeFilter);

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (organizationId) {
        headers['x-organization-id'] = organizationId;
      }

      const res = await apiFetch(`/api/v1/settings/integrations/brightblue/events?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.items || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, eventTypeFilter, organizationId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const getStatusBadge = (status: WebhookEvent['status']) => {
    const variants: Record<WebhookEvent['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
      COMPLETED: 'default',
      PROCESSING: 'secondary',
      PENDING: 'outline',
      FAILED: 'destructive',
    };
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const eventTypes = [
    'all',
    'product.create',
    'product.update',
    'product.delete',
    'category.create',
    'category.update',
    'category.delete',
    'machine.create',
    'machine.update',
    'transaction.create',
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Webhook Events</CardTitle>
        <CardDescription>View all incoming webhook events from Bright.Blue</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 mb-4">
          <div className="w-48">
            <Select value={statusFilter} onValueChange={(v) => { setPage(1); setStatusFilter(v); }}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="PROCESSING">Processing</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Select value={eventTypeFilter} onValueChange={(v) => { setPage(1); setEventTypeFilter(v); }}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by event type" />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type === 'all' ? 'All Event Types' : type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={fetchEvents} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {loading && events.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Loading events...</div>
        ) : events.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No webhook events found.</div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-3 font-medium">Event Type</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Received</th>
                  <th className="text-left p-3 font-medium">Processed</th>
                  <th className="text-left p-3 font-medium">Event ID</th>
                  <th className="text-left p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-t hover:bg-muted/25">
                    <td className="p-3 font-mono text-xs">{event.eventType}</td>
                    <td className="p-3">{getStatusBadge(event.status)}</td>
                    <td className="p-3 text-xs">{formatDateTime(event.createdAt)}</td>
                    <td className="p-3 text-xs">
                      {event.processedAt ? formatDateTime(event.processedAt) : '-'}
                    </td>
                    <td className="p-3 font-mono text-xs truncate max-w-[150px]" title={event.eventId}>
                      {event.eventId}
                    </td>
                    <td className="p-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedEvent(event)}
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({total} total events)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>

        <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Event Details</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Event ID:</span>
                    <p className="font-mono text-xs break-all">{selectedEvent.eventId}</p>
                  </div>
                  <div>
                    <span className="font-medium">Event Type:</span>
                    <p className="font-mono">{selectedEvent.eventType}</p>
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>
                    <div className="mt-1">{getStatusBadge(selectedEvent.status)}</div>
                  </div>
                  <div>
                    <span className="font-medium">Received:</span>
                    <p>{formatDateTime(selectedEvent.createdAt)}</p>
                  </div>
                  {selectedEvent.processedAt && (
                    <div>
                      <span className="font-medium">Processed:</span>
                      <p>{formatDateTime(selectedEvent.processedAt)}</p>
                    </div>
                  )}
                </div>
                {selectedEvent.error && (
                  <div>
                    <span className="font-medium text-sm">Error:</span>
                    <pre className="mt-1 p-3 bg-destructive/10 text-destructive rounded text-xs overflow-auto">
                      {selectedEvent.error}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default WebhookEventsList;
